import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FilterService } from "../services/filter.service";
import { SavedFilter, Filter } from "../models/filter.model";
import { FilterFormComponent } from "../filter-form/filter-form.component";
import { StateService } from "../services/state.service";
import { take } from "rxjs";
import { SavedFilterListComponent } from "../ui/molecule/saved-filter-list/saved-filter-list.component";
import { MessageService } from "primeng/api";
import { IUser } from "../models/user.model";
import { IPanel } from "../util/common.models";
import {
  cleanPanelFilters
} from "../util/common.util";

@Component({
  selector: "app-filters",
  templateUrl: "./filters.component.html",
  styleUrl: "./filters.component.scss",
})
export class FiltersComponent {
  @Input() visible: boolean = false;
  @Input() appliedFilter: SavedFilter | null = null;
  @Input() defaultFilterId: number | null = null;
  @Input() panels: IPanel[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() applied = new EventEmitter<number>();
  @Output() applyFilters = new EventEmitter<Filter[]>();

  @ViewChild(FilterFormComponent) filterFormComponent!: FilterFormComponent;
  @ViewChild(SavedFilterListComponent)
  savedFilterListComponent!: SavedFilterListComponent;

  savedFilters: SavedFilter[] = [];
  editedFilter: SavedFilter | null = null;
  filterToBeSaved: SavedFilter | null = null;
  activeTabIndex: number = 0;
  disableApplyORSave: boolean = true;
  currentUser: IUser | null = null;

  loadingSavedFilters: boolean = true;

  constructor(
    public filterService: FilterService,
    public stateService: StateService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    setTimeout(() => {
      this.currentUser = this.stateService.getUser();
      this.filterService
        .getAllSavedFilters()
        .pipe(take(1))
        .subscribe({
          next: (data) => {
            this.savedFilters = data;
            this.loadingSavedFilters = false;
          },
          error: (err) => {},
        });
    }, 5000);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["panels"]) {
      this.savedFilters = this.savedFilters.map((filter) => ({
        ...filter,
        filterValues: cleanPanelFilters(filter.filterValues, this.panels),
      }));
    }
  }

  clearAppliedFilters() {
    // this.filterFormComponent.formReset();
    this.appliedFilter = null;
    // this.editedFilter = null;
    this.applied.emit(0);
  }

  handleDisableApplyORSave(bool: boolean) {
    this.disableApplyORSave = bool;
  }

  handleApply() {
    this.filterFormComponent.clickApplyFilter();
  }

  handleApplyFilter(filters: Filter[]) {
    this.appliedFilter = { filterValues: filters };
    this.applied.emit(filters.length);
  }

  handleApplyFilterFromSave(savedFilter: SavedFilter) {
    this.appliedFilter = savedFilter;
    this.activeTabIndex = 0;
    this.applied.emit(this.appliedFilter.filterValues.length);
    this.closeSidebar();
  }

  setAsDefaultFilterAndApply(savedFilter: SavedFilter) {
    this.appliedFilter = savedFilter;
    this.activeTabIndex = 0;
    this.filterService.setDefaultFilter(savedFilter.id!).subscribe({
      next: (res) => {
        this.messageService.add({
          severity: "success",
          summary: "Success",
          detail: res.message,
        });
      },
      error: (err) => {},
    });
    this.applied.emit(this.appliedFilter.filterValues.length);
    this.closeSidebar();
  }

  handleEditFilter(editedFilter: SavedFilter) {
    this.editedFilter = editedFilter;
    this.activeTabIndex = 0;
  }

  handleSave() {
    this.filterFormComponent.clickSaveFilter();
  }

  handleAppliedFilterRemoveSingle(chip: { key: string; value: string }) {
    if (this.editedFilter)
      this.editedFilter.filterValues = this.editedFilter.filterValues.filter(
        (af) => af.key !== chip.key
      );
  }

  getAllSavedFilters() {
    this.filterService.getAllSavedFilters().subscribe({
      next: (data) => {
        this.savedFilters = data;
      },
      error: (err) => {},
    });
  }

  handleSaveFilterFromSave(filter: SavedFilter | any) {
    this.editedFilter = null;
    this.activeTabIndex = 1;
    this.filterService
      .saveFilter(filter)
      .pipe(take(1))
      .subscribe({
        next: () => this.getAllSavedFilters(),
        error: (error) => {
          this.getAllSavedFilters();
        },
      });
  }

  handleDeleteFilter(filter: SavedFilter) {
    if (filter.id)
      this.filterService
        .delete(filter)
        .pipe(take(1))
        .subscribe({
          next: (data) => {
            this.filterService.getAllSavedFilters().subscribe({
              next: (data) => {
                this.savedFilters = data;
              },
              error: (err) => {},
            });
          },
          error: (error) => {},
        });
    else this.savedFilters = this.savedFilters.filter((f) => f != filter);
  }

  handleSaveFilterFromForm(filter: SavedFilter) {
    if (filter.filterValues.length > 0) {
      this.activeTabIndex = 1;
    }
    if (this.editedFilter && filter.id && this.savedFilters) {
      this.savedFilters = this.savedFilters?.map((f) => {
        if (f.id == filter.id) {
          return { ...filter, isEditing: true };
        }
        return { ...f };
      });
    } else {
      let editedFilter = this.savedFilters.filter((sf) => sf.isEditing == true);
      if (!editedFilter.length) {
        this.savedFilters = [
          ...(this.savedFilters ?? []),
          {
            filterValues: filter.filterValues,
            isEditing: true,
            privateFilter: filter.privateFilter,
          },
        ];
      } else {
        this.savedFilters[this.savedFilters?.length - 1].filterValues =
          filter?.filterValues;
        this.savedFilters[this.savedFilters?.length - 1].privateFilter =
          filter?.privateFilter;
      }
    }
  }

  handleCancel() {
    this.filterFormComponent.clickCancel();
    this.closeSidebar();
    this.filterService
      .getAllSavedFilters()
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          this.savedFilters = data;
          this.loadingSavedFilters = false;
        },
        error: (err) => {},
      });
  }

  closeSidebar(): void {
    this.savedFilterListComponent.close();
    this.activeTabIndex = 0;
    // this.editedFilter = null
    this.close.emit();
  }

  handleResetEvent() {
    this.savedFilterListComponent.close();
    this.appliedFilter = null;
    this.editedFilter = null;
    this.savedFilters = this.savedFilters.filter((sf) => sf.id);
  }

  handleApplyFilters(filters: Filter[]) {
    this.applyFilters.emit(filters);
  }

  removeDefaultFilterAndApply(filter: SavedFilter) {
    this.defaultFilterId = null;
    this.filterFormComponent.removeDefaultFilter(filter);
    if (this.appliedFilter) {
      delete this.appliedFilter.id;
    }
  }
}
